// Reload an Inertia page in place and flash what changed: the companion's
// live refresh (@shared/lib/companion-refresh) and server-pushed refreshes
// such as a quote's status change (CONEK-9275).

import {
  highlightChanges,
  snapshotTexts,
} from "@shared/lib/companion-change-highlight"

// Inertia v2's router.reload() keeps scroll and component state by default
// (its options type omits preserveScroll / preserveState); the only option
// passed is the success hook that flashes what changed.
export interface ReloadRouter {
  reload: (options: { onSuccess: () => void }) => void
}

// onSuccess fires once the new page object is set; React commits the render
// after it. Two frames later the DOM shows the new props.
const afterRender = (fn: () => void) =>
  requestAnimationFrame(() => requestAnimationFrame(fn))

// Never reload under a field the user is typing in (the companion panel's own
// input does not count): a reload would throw their input away.
export const isEditingPage = (): boolean => {
  const el = document.activeElement
  if (!(el instanceof HTMLElement)) return false
  if (el.closest('[data-controller~="ai--companion"]')) return false
  return el.isContentEditable || el.matches("input, textarea, select")
}

// Reloads the page's props in place and flashes the text that changed. Also
// the live-delivery cue of server-pushed refreshes (CONEK-9275).
export function reloadInPlace(router: ReloadRouter) {
  // The page is `#app`; the companion panel is its sibling, so its own
  // transcript never counts as a change.
  const root = document.getElementById("app")
  const before = root ? snapshotTexts(root) : null
  router.reload({
    onSuccess: () => {
      if (root && before) afterRender(() => highlightChanges(root, before))
    },
  })
}
